import * as XLSX from "xlsx";
import { SEASON_MONTH_ORDER } from "./seasonComparisonCalc";

/* Season Comparison — Fase 4 (Excel export). Builds the workbook by
   hand with XLSX.utils.aoa_to_sheet + ws['!merges'] instead of the
   shared xlsxExport.js wrapper (which only emits flat, single-header-
   row sheets via json_to_sheet) — this layout needs 2-row merged
   group headers matching the user's real reference sheet.

   COLOR LIMITATION: the installed `xlsx` package (SheetJS Community
   Edition, 0.18.5) silently drops cell-level styles (font color,
   fill) on write — confirmed empirically (write a colored cell, read
   it back, the style resets to "none"). Real per-cell conditional
   coloring is a Pro-only feature. Instead, the Δ Profit / Δ Acumulado
   columns use Excel's OWN built-in conditional-color number-format
   syntax ("[Green]...;[Red]...;..." inside the format string itself)
   — also confirmed empirically to survive the write/read round-trip,
   since it's a numFmt feature, not a cell-style feature. This gives
   real native green/red coloring in Excel without needing style
   support at all. Zero/blank deltas get no color (Excel has no
   built-in named "gray"; default text color reads as neutral, which
   is what "gris neutro" means in practice). */

var FMT_INT = "#,##0";
var FMT_MONEY = "$#,##0.00";
var FMT_NUM2 = "0.00";
var FMT_MONEY_DELTA = "[Green]$#,##0.00;[Red]-$#,##0.00;$#,##0.00";

/* aoa_to_sheet SKIPS null/undefined array entries entirely (no cell
   gets created at that address) rather than writing a blank/placeholder
   cell — so every nullable numeric value must be pre-converted to the
   literal "—" string before it goes into the AOA, or the cell simply
   won't exist in the output sheet at all. */
function numOrDash(v) { return v == null ? "—" : v; }

/* seasonResults: [{ season, data: { rows }, hasDelta, deltas, label, footer }]
   hasDelta: true only for whichever season is the most-recent of the
   CURRENT VISUAL SELECTION (see SeasonComparison.js's deltaTargetSeasons)
   — NOT the same as "this season is Open". A Closed season can have
   hasDelta=true if the user's filter excludes the Open season entirely.
   deltas: month-keyed lookup ({8: {deltaProfit, deltaAcumulado}, ...}), only present when hasDelta.
   footer: { cases, profit, profitBox, profitDay, profitHour } from computeSeasonFooter. */
/* Pure sheet builder — no file I/O, so it's unit-testable without
   touching disk or hitting SheetJS's browser-vs-Node writeFile branch
   (which behaves differently under jsdom). exportSeasonComparisonXlsx
   below just wraps this in a workbook and writes it. */
export function buildSeasonComparisonSheet({ t, historicalAccumulated, histFooter, seasonResults }) {
  var monthCol = 0;
  var histCol = 1; // Cases, Profit, Acumulado -> cols 1-3
  var seasonStartCols = [];
  var col = 4;
  seasonResults.forEach(function (sr) {
    seasonStartCols.push(col);
    col += sr.hasDelta ? 9 : 7;
  });
  var totalCols = col;

  var HIST_LABEL_ROW = 0, SUBHEADER_ROW = 1, FIRST_MONTH_ROW = 2;
  var LAST_MONTH_ROW = FIRST_MONTH_ROW + 11;
  var TOTAL_ROW = LAST_MONTH_ROW + 1;

  var aoa = [];
  for (var r = 0; r <= TOTAL_ROW; r++) aoa.push(new Array(totalCols).fill(""));

  aoa[HIST_LABEL_ROW][monthCol] = t.colMonth;
  aoa[HIST_LABEL_ROW][histCol] = t.historicalAccumulated;
  aoa[SUBHEADER_ROW][histCol] = t.colCases;
  aoa[SUBHEADER_ROW][histCol + 1] = t.colProfit;
  aoa[SUBHEADER_ROW][histCol + 2] = t.colAcumulado;

  seasonResults.forEach(function (sr, i) {
    var c = seasonStartCols[i];
    aoa[HIST_LABEL_ROW][c] = sr.label;
    aoa[SUBHEADER_ROW][c + 0] = t.colMonth;
    aoa[SUBHEADER_ROW][c + 1] = t.colCases;
    aoa[SUBHEADER_ROW][c + 2] = t.colProfit;
    aoa[SUBHEADER_ROW][c + 3] = t.colAcumulado;
    aoa[SUBHEADER_ROW][c + 4] = t.colProfitBox;
    aoa[SUBHEADER_ROW][c + 5] = t.colProfitDay;
    aoa[SUBHEADER_ROW][c + 6] = t.colProfitHour;
    if (sr.hasDelta) {
      aoa[SUBHEADER_ROW][c + 7] = t.colDeltaProfit;
      aoa[SUBHEADER_ROW][c + 8] = t.colDeltaAcumulado;
    }
  });

  SEASON_MONTH_ORDER.forEach(function (m, idx) {
    var rowIdx = FIRST_MONTH_ROW + idx;
    var hist = historicalAccumulated.find(function (x) { return x.month === m; });
    aoa[rowIdx][monthCol] = t.months[m - 1];
    aoa[rowIdx][histCol] = numOrDash(hist.cases);
    aoa[rowIdx][histCol + 1] = numOrDash(hist.profit);
    aoa[rowIdx][histCol + 2] = numOrDash(hist.acumulado);
    seasonResults.forEach(function (sr, i) {
      var c = seasonStartCols[i];
      var row = sr.data.rows.find(function (x) { return x.month === m; });
      aoa[rowIdx][c + 0] = row.year != null ? t.months[m - 1] + " " + row.year : "";
      aoa[rowIdx][c + 1] = numOrDash(row.cases);
      aoa[rowIdx][c + 2] = numOrDash(row.profit);
      aoa[rowIdx][c + 3] = numOrDash(row.acumulado);
      aoa[rowIdx][c + 4] = numOrDash(row.profitBox);
      aoa[rowIdx][c + 5] = numOrDash(row.profitDay);
      aoa[rowIdx][c + 6] = numOrDash(row.profitHour);
      if (sr.hasDelta) {
        var delta = sr.deltas[m];
        aoa[rowIdx][c + 7] = numOrDash(delta.deltaProfit);
        aoa[rowIdx][c + 8] = numOrDash(delta.deltaAcumulado);
      }
    });
  });

  aoa[TOTAL_ROW][monthCol] = t.totalRow;
  aoa[TOTAL_ROW][histCol] = numOrDash(histFooter.cases);
  aoa[TOTAL_ROW][histCol + 1] = numOrDash(histFooter.profit);
  aoa[TOTAL_ROW][histCol + 2] = "—";
  seasonResults.forEach(function (sr, i) {
    var c = seasonStartCols[i];
    aoa[TOTAL_ROW][c + 0] = "—";
    aoa[TOTAL_ROW][c + 1] = numOrDash(sr.footer.cases);
    aoa[TOTAL_ROW][c + 2] = numOrDash(sr.footer.profit);
    aoa[TOTAL_ROW][c + 3] = "—";
    aoa[TOTAL_ROW][c + 4] = numOrDash(sr.footer.profitBox);
    aoa[TOTAL_ROW][c + 5] = numOrDash(sr.footer.profitDay);
    aoa[TOTAL_ROW][c + 6] = numOrDash(sr.footer.profitHour);
    if (sr.hasDelta) { aoa[TOTAL_ROW][c + 7] = "—"; aoa[TOTAL_ROW][c + 8] = "—"; }
  });

  var ws = XLSX.utils.aoa_to_sheet(aoa);

  /* Re-write every numeric cell with its real value + number format —
     aoa_to_sheet already typed them correctly (number vs string) from
     the raw JS values above; this pass only attaches `.z`. Text cells
     (headers, month labels, "—" placeholders, "TOTAL") are left as
     aoa_to_sheet produced them. */
  function applyMoneyCol(c, isDelta) {
    for (var r = FIRST_MONTH_ROW; r <= TOTAL_ROW; r++) {
      var addr = XLSX.utils.encode_cell({ r: r, c: c });
      var cell = ws[addr];
      if (cell && cell.t === "n") cell.z = isDelta ? FMT_MONEY_DELTA : FMT_MONEY;
    }
  }
  function applyIntCol(c) {
    for (var r = FIRST_MONTH_ROW; r <= TOTAL_ROW; r++) {
      var addr = XLSX.utils.encode_cell({ r: r, c: c });
      var cell = ws[addr];
      if (cell && cell.t === "n") cell.z = FMT_INT;
    }
  }
  function applyNum2Col(c) {
    for (var r = FIRST_MONTH_ROW; r <= TOTAL_ROW; r++) {
      var addr = XLSX.utils.encode_cell({ r: r, c: c });
      var cell = ws[addr];
      if (cell && cell.t === "n") cell.z = FMT_NUM2;
    }
  }
  applyIntCol(histCol);
  applyMoneyCol(histCol + 1, false);
  applyMoneyCol(histCol + 2, false);
  seasonResults.forEach(function (sr, i) {
    var c = seasonStartCols[i];
    applyIntCol(c + 1);
    applyMoneyCol(c + 2, false);
    applyMoneyCol(c + 3, false);
    applyNum2Col(c + 4);
    applyNum2Col(c + 5);
    applyNum2Col(c + 6);
    if (sr.hasDelta) {
      applyMoneyCol(c + 7, true);
      applyMoneyCol(c + 8, true);
    }
  });

  /* Merges: leftmost Month column spans both header rows (rowSpan 2);
     "Acumulado Histórico" spans its 3 columns; each season name spans
     its own 7 or 9 columns. */
  var merges = [
    { s: { r: HIST_LABEL_ROW, c: monthCol }, e: { r: SUBHEADER_ROW, c: monthCol } },
    { s: { r: HIST_LABEL_ROW, c: histCol }, e: { r: HIST_LABEL_ROW, c: histCol + 2 } },
  ];
  seasonResults.forEach(function (sr, i) {
    var c = seasonStartCols[i];
    var w = sr.hasDelta ? 9 : 7;
    merges.push({ s: { r: HIST_LABEL_ROW, c: c }, e: { r: HIST_LABEL_ROW, c: c + w - 1 } });
  });
  ws["!merges"] = merges;

  /* Reasonable column widths so the file isn't unreadable on open —
     month/label columns wider, numeric columns fixed. */
  var cols = [{ wch: 10 }];
  for (var cc = 1; cc < totalCols; cc++) cols.push({ wch: 12 });
  ws["!cols"] = cols;

  var range = { s: { r: 0, c: 0 }, e: { r: TOTAL_ROW, c: totalCols - 1 } };
  ws["!ref"] = XLSX.utils.encode_range(range);

  return { ws: ws, meta: { monthCol: monthCol, histCol: histCol, seasonStartCols: seasonStartCols, FIRST_MONTH_ROW: FIRST_MONTH_ROW, TOTAL_ROW: TOTAL_ROW } };
}

export function exportSeasonComparisonXlsx(params) {
  var built = buildSeasonComparisonSheet(params);
  var wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, built.ws, "Season Comparison".slice(0, 31));

  var d = new Date();
  var iso = d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0");
  XLSX.writeFile(wb, "Season Comparison " + iso + ".xlsx");
}
